import { computed, nextTick, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import workflowApi from '@/api/workflowApi'
import cardTypesApi from '@/api/cardTypesApi'
import safeStructuredClone from '@/utils/clone'
import { EventBus } from '@/event-bus/event-bus'
import { DomainModelRole } from '@/constants/cardTypes'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import { sanitizeCardDescription } from '@/utils/sanitize'
import { DataType, FieldType, SchemaType } from '@/constants/schema'
import { isSingularPluralMatch } from '@/utils/fieldNameMatch'
import { fieldHasNoMatchingEntityField } from '@/shared/utils/fieldEntityMatch'

let pendingSchemaChange = null

const applyPendingChanges = (schemas) => {
  if (pendingSchemaChange) {
    pendingSchemaChange(schemas)
    pendingSchemaChange = null
  }
}

const shouldDeferSchemaUpdate = (e) => {
  return !!e.relatedTarget?.classList?.contains('defers-schema-blur')
}

const useDomainModel = () => {
  const loadingDeleteModel = ref(false)

  const store = useStore()
  const route = useRoute()
  const workflow = computed(() => store.state.workflow.workflow)
  const svgGraph = computed(() => safeStructuredClone(store.getters['svgGraphData/svgGraphData']))
  const showEntities = computed(() => store.state.domainModelShowEntities)

  const validateFieldName = (event, newValue, originalValue) => {
    const maxLength = 30
    if (newValue.length > maxLength) {
      alert(`Data field name must be a maximum of ${maxLength} characters.`)
      diagramHelpers.methods.restoreValue(event, originalValue)
      return false
    }
    return true
  }

  const cardTypeColor = (cardType) => {
    return cardTypesApi.getBackgroundColour(cardType)
  }

  const getSchemaFieldsForCard = (event, cardId) => {
    const card = event.cards?.find((c) => c.id === cardId)
    if (!card?.schemaId) {
      return { fields: [], schema: null, hasSchema: false }
    }
    const schema = workflow.value.schemas?.find((s) => s.id === card.schemaId)

    return { fields: schema?.fields || [], schema, hasSchema: !!schema }
  }

  const countRelatedEntityLevels = (fieldList) => {
    const hasRelated = fieldList.some((field) => field.relatedEntityId)
    if (!hasRelated) {
      return 0
    }

    let maxNestedLevels = 0
    for (const field of fieldList) {
      if (field.fields?.length) {
        maxNestedLevels = Math.max(maxNestedLevels, countRelatedEntityLevels(field.fields))
      }
    }

    return 1 + maxNestedLevels
  }

  const countFields = (fieldList) => {
    return fieldList.reduce((acc, field) => {
      acc += 1
      if (field.relatedEntityId && field.fields?.length) {
        acc += countFields(field.fields)
      }

      return acc
    }, 0)
  }

  const getCommandCardHeight = (event, card) => {
    const cardData = getSchemaFieldsForCard(event, card.id)
    const { hasSchema, fields } = cardData || {}

    if (!showEntities.value || !hasSchema) {
      return '82px'
    }

    const fieldsCount = countFields(fields)
    const relatedEntityLevels = countRelatedEntityLevels(fields)

    // 74 - top/bottom borders (3px) + padding (24px) + input height (31px) + toolbar height (16px)
    // 16 - fields wrapper padding (16px)
    return `${74 + (fieldsCount ? 16 : 0) + (relatedEntityLevels * 16) + (fieldsCount * 25)}px`
  }

  const getReadModelCardHeight = (event, card, baseHeight = 0) => {
    const cardData = getSchemaFieldsForCard(event, card.id)
    const { hasSchema, fields, schema } = cardData || {}

    const hasLinkedEntity = schema?.entityId
    const isDomainEvent = schema?.type === SchemaType.DOMAIN_EVENT

    if (!showEntities.value) {
      // 22 - bounded context selector when entity is linked
      const bcSelectorAndTextareaHeight = (hasLinkedEntity ? 22 : 0) + baseHeight

      return `${(!hasSchema ? 110 : 140 + bcSelectorAndTextareaHeight) + (!card.isInContext ? 16 : 0)}px`
    }

    if (!hasSchema) {
      return `${110 + (!card.isInContext ? 16 : 0)}px`
    }

    const fieldsCount = countFields(fields)
    const relatedEntityLevels = countRelatedEntityLevels(fields)

    if (isDomainEvent) {
      const bcSelectorHeight = hasLinkedEntity ? 22 : 8

      // 96 - top/bottom borders (3px) + padding (24px) + input and entity height (53px) + toolbar height (16px)
      // 32 - space for arrows or add more (32px)
      // 16 - fields wrapper padding py-2 (16px)
      return `${96 + 32 + (fieldsCount ? 16 : 0) + (relatedEntityLevels * 16) + (fieldsCount * 25) + bcSelectorHeight}px`
    }

    // 43 - top/bottom borders (3px) + padding (24px) + toolbar height (16px)
    // 16 - fields wrapper padding py-2 (16px)
    // 32 for space for arrows or add more
    // 57 for input and chip in query edit mode (31px + 18px + 8px gap)
    // 22 for not in bounded context queries (top border has extra 22px)
    // 26 for bounded context selector when entity is linked (18px + 8px padding)
    const bcSelectorHeight = hasLinkedEntity ? 26 : 0
    const initialHeight = 43 + (fieldsCount ? 16 : 0) + 32 + 57 + bcSelectorHeight +
      (!card.isInContext ? 16 : 0)

    return `${initialHeight + (relatedEntityLevels * 16) + (fieldsCount * 25) + baseHeight}px`
  }

  const getDomainEventCardHeight = (schema, linkedReadModels) => {
    const fieldsCount = countFields(schema.fields)
    const relatedEntityLevels = countRelatedEntityLevels(schema.fields)

    // 8 - fields wrapper padding
    // 25 - field row
    // 16 - add subfield button
    const fieldsHeight = (fieldsCount ? 8 : 0) + (fieldsCount * 25) + (relatedEntityLevels * 16)
    const readModelCount = linkedReadModels.value.length
    const addFieldMargin = (readModelCount > 0 ? 8 : 0)
    const chipRows = Math.ceil(readModelCount / 2)

    // 22px label with margin + rows of chips (16px chip) + 4px gap between rows, ~2 chips per row
    const projectionsHeight = readModelCount > 0 ? 22 + (chipRows * 16) + ((chipRows - 1) * 4) : 0

    // 92 - top/bottom border (3px) + padding (24px) + name input with margin (29px) +
    // entity selector with margin (20px) + add field button (16px)
    return `${92 + fieldsHeight + addFieldMargin + projectionsHeight}px`
  }

  const getQueryCardHeight = (
    schema,
    linkedDomainEvents,
    suggestedDomainEvents,
    hasAvailableDomainEvents,
    descriptionHeight = 0
  ) => {
    const fieldsCount = countFields(schema.fields)
    const relatedEntityLevels = countRelatedEntityLevels(schema.fields)

    // 8 - fields wrapper padding
    // 25 - field row
    // 16 - add subfield button
    const fieldsHeight = (fieldsCount ? 8 : 0) + (fieldsCount * 25) + (relatedEntityLevels * 16)

    // domain event chips: linked + suggested + 1 for "+ Domain Event" button
    const linkedCount = linkedDomainEvents.value.length
    const suggestedCount = suggestedDomainEvents.value.length
    const totalChips = linkedCount + suggestedCount + (hasAvailableDomainEvents.value ? 1 : 0)
    const chipRows = totalChips > 0 ? Math.ceil(totalChips / 2) : 0
    const domainEventChipsHeight = chipRows > 0 ? (chipRows * 16) + ((chipRows - 1) * 4) : 0

    // 108 - top/bottom border (3px) + padding (24px) + name input with margin (29px) +
    // entity selector with margin (20px) + add field button with margin (24px) + description margin (8px)
    return `${108 + descriptionHeight + fieldsHeight + domainEventChipsHeight}px`
  }

  const removeCardAndModel = async (event, card) => {
    loadingDeleteModel.value = true
    await workflowApi.deleteCardAndModel(event, card, svgGraph.value, workflow.value)
    loadingDeleteModel.value = false
  }

  const getRelatedEntity = (field) => {
    return workflow.value.schemas?.find((entity) => entity.id === field.relatedEntityId)
  }

  const getMatchingFieldInEntity = (entity, fieldName) => {
    if (!entity) {
      return null
    }

    const entityFields = entity.fields || []
    const matchingField = entityFields.find((field) => field.name === fieldName) ||
      entityFields.find((field) => isSingularPluralMatch(field.name, fieldName))
    let relatedEntity

    if (matchingField && matchingField.relatedEntityId) {
      relatedEntity = (workflow.value.schemas ?? []).find((item) => item.id === matchingField.relatedEntityId)
    }

    return matchingField ? { ...matchingField, relatedEntity } : null
  }

  const hasFieldIssue = (entityId, field) =>
    fieldHasNoMatchingEntityField(entityId, field, workflow.value.schemas || [])

  const getCardAndSchema = (eventId, cardId) => {
    const card = svgGraph.value[eventId]?.cards?.find((c) => c.id === cardId)
    if (!card?.schemaId) {
      return null
    }

    const schemas = safeStructuredClone(workflow.value.schemas || [])
    applyPendingChanges(schemas)
    const schemaIndex = schemas.findIndex((s) => s.id === card.schemaId)
    if (schemaIndex === -1) {
      return null
    }

    return { card, schemas, schemaIndex }
  }

  const getFieldAtPath = (fields, path) => {
    let current = { fields }
    for (const index of path) {
      if (!current.fields || index < 0 || index >= current.fields.length) {
        return null
      }
      current = current.fields[index]
    }
    return current
  }

  const addFieldAtPath = async (event, cardId, path) => {
    const result = getCardAndSchema(event.id, cardId)
    if (!result) {
      return
    }

    const { schemas, schemaIndex } = result
    const parent = getFieldAtPath(schemas[schemaIndex].fields, path)
    if (!parent) {
      return
    }
    const updatedFields = parent.fields ?? []
    updatedFields.push({
      name: 'NEW SUB FIELD ' + (updatedFields.length + 1)
    })
    parent.fields = updatedFields

    await workflowApi.updateSchemas({
      schemas,
      workflow: workflow.value
    })
  }

  const addSubField = async (event, cardId, index) => {
    return addFieldAtPath(event, cardId, [index])
  }

  const addSubSubField = async (event, cardId, fieldIndex, subFieldIndex) => {
    return addFieldAtPath(event, cardId, [fieldIndex, subFieldIndex])
  }

  const blurFieldAtPath = async (event, workflowEvent, schemaId, path) => {
    if (!schemaId || !path.length) {
      return
    }

    const currentSchemas = workflow.value.schemas || []
    const schemaIndex = currentSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const field = getFieldAtPath(currentSchemas[schemaIndex].fields, path)
    if (!field) {
      return
    }

    const newName = (event.target.innerText || '').trim()
    const oldName = field.name

    if (newName === oldName || !validateFieldName(event, newName, oldName)) {
      return
    }

    const applyChange = (targetSchemas) => {
      const idx = targetSchemas.findIndex((s) => s.id === schemaId)
      if (idx !== -1) {
        const targetField = getFieldAtPath(targetSchemas[idx].fields, path)
        if (targetField) {
          targetField.name = newName
        }
      }
    }

    if (shouldDeferSchemaUpdate(event)) {
      pendingSchemaChange = applyChange
      return
    }

    const schemas = safeStructuredClone(currentSchemas)
    applyPendingChanges(schemas)
    applyChange(schemas)

    await nextTick()

    await workflowApi.updateSchemas({
      schemas,
      workflow: workflow.value
    })
  }

  const removeFieldAtPath = async (event, cardId, path) => {
    const result = getCardAndSchema(event.id, cardId)
    if (!result || !path.length) {
      return
    }

    const { schemas, schemaIndex } = result

    if (path.length === 1 && schemas[schemaIndex].fields.length === 1) {
      schemas.splice(schemaIndex, 1)
      const updatedSvgGraph = safeStructuredClone(svgGraph.value)
      const cardIndex = updatedSvgGraph[event.id]?.cards?.findIndex((c) => c.id === cardId)
      if (cardIndex >= 0) {
        updatedSvgGraph[event.id].cards[cardIndex].schemaId = null
      }
      await workflowApi.updateWorkflow({
        svgGraph: updatedSvgGraph,
        schemas,
        workflow: workflow.value
      }, true)
    } else {
      const parentPath = path.slice(0, -1)
      const fieldIndex = path[path.length - 1]
      const parent = parentPath.length === 0
        ? schemas[schemaIndex]
        : getFieldAtPath(schemas[schemaIndex].fields, parentPath)
      if (parent?.fields) {
        parent.fields.splice(fieldIndex, 1)
      }
      await workflowApi.updateSchemas({
        schemas,
        workflow: workflow.value
      })
    }
  }

  const selectRelatedEntityAtPath = async (event, cardId, path, entity) => {
    const result = getCardAndSchema(event.id, cardId)
    if (!result || !path.length) {
      return
    }

    const { schemas, schemaIndex } = result
    const field = getFieldAtPath(schemas[schemaIndex].fields, path)
    if (!field) {
      return
    }

    field.relatedEntityId = entity?.id || null

    await workflowApi.updateSchemas({
      schemas,
      workflow: workflow.value
    })

    EventBus.$emit('refresh-entity-relationship-diagram')
  }

  const selectCardinalityAtPath = async (eventId, cardId, path, cardinality) => {
    const result = getCardAndSchema(eventId, cardId)
    if (!result || !path.length) {
      return
    }

    const { schemas, schemaIndex } = result
    const field = getFieldAtPath(schemas[schemaIndex].fields, path)
    if (!field) {
      return
    }

    field.cardinality = cardinality

    await workflowApi.updateSchemas({
      schemas,
      workflow: workflow.value
    })
  }

  const hideFieldAtPath = async (event, cardId, path) => {
    const result = getCardAndSchema(event.id, cardId)
    if (!result || !path.length) {
      return
    }

    const { schemas, schemaIndex } = result
    const field = getFieldAtPath(schemas[schemaIndex].fields, path)
    if (!field) {
      return
    }

    field.hideInForm = !field.hideInForm

    await workflowApi.updateSchemas({
      schemas,
      workflow: workflow.value
    })
  }

  const blurField = async (event, workflowEvent, schemaId, fieldIndex) => {
    return blurFieldAtPath(event, workflowEvent, schemaId, [fieldIndex])
  }

  const blurSubField = async (event, workflowEvent, cardId, fieldIndex, subFieldIndex) => {
    const card = svgGraph.value[workflowEvent.id]?.cards?.find((c) => c.id === cardId)
    return blurFieldAtPath(event, workflowEvent, card?.schemaId, [fieldIndex, subFieldIndex])
  }

  const blurSubSubField = async (event, workflowEvent, cardId, fieldIndex, subFieldIndex, subSubFieldIndex) => {
    const card = svgGraph.value[workflowEvent.id]?.cards?.find((c) => c.id === cardId)
    return blurFieldAtPath(event, workflowEvent, card?.schemaId, [fieldIndex, subFieldIndex, subSubFieldIndex])
  }

  const removeSubField = async (event, cardId, index, subIndex) => {
    return removeFieldAtPath(event, cardId, [index, subIndex])
  }

  const removeSubSubField = async (event, cardId, fieldIndex, subFieldIndex, subSubFieldIndex) => {
    return removeFieldAtPath(event, cardId, [fieldIndex, subFieldIndex, subSubFieldIndex])
  }

  const onSelectRelatedEntity = async (event, cardId, fieldIndex, entity) => {
    return selectRelatedEntityAtPath(event, cardId, [fieldIndex], entity)
  }

  const onSelectRelatedSubEntity = async (event, cardId, fieldIndex, subFieldIndex, entity) => {
    return selectRelatedEntityAtPath(event, cardId, [fieldIndex, subFieldIndex], entity)
  }

  const selectSubFieldCardinality = async (eventId, cardId, fieldIndex, subFieldIndex, cardinality) => {
    return selectCardinalityAtPath(eventId, cardId, [fieldIndex, subFieldIndex], cardinality)
  }

  const addField = async (eventId, cardId) => {
    const targetEventId = eventId || route.params.eventId
    if (!targetEventId) {
      return
    }

    const result = getCardAndSchema(targetEventId, cardId)

    if (result) {
      const { schemas, schemaIndex } = result
      schemas[schemaIndex].fields.push({
        name: 'NEW FIELD ' + (schemas[schemaIndex].fields.length + 1)
      })

      await workflowApi.updateSchemas({
        schemas,
        workflow: workflow.value
      })
    } else {
      const card = svgGraph.value[targetEventId]?.cards?.find((c) => c.id === cardId)
      if (!card) {
        return
      }

      const domainModelRole = card.cardType?.domainModelRole
      const schemaName = sanitizeCardDescription(card.description)

      const schemaTypeMap = {
        [DomainModelRole.COMMAND]: SchemaType.COMMAND,
        [DomainModelRole.DOMAIN_EVENT]: SchemaType.DOMAIN_EVENT
      }

      const newSchema = {
        name: schemaName,
        type: schemaTypeMap[domainModelRole] || SchemaType.QUERY,
        fields: [{ name: 'NEW FIELD 1' }]
      }

      await workflowApi.addSchemaAndLinkCard({
        schema: newSchema,
        cardId,
        eventId: targetEventId,
        workflow: workflow.value,
        svgGraph: svgGraph.value
      })
    }
  }

  const removeField = async (event, cardId, index) => {
    return removeFieldAtPath(event, cardId, [index])
  }

  const isAnyFieldChecked = (fields) => {
    for (const field of (fields || [])) {
      if (field.checked) {
        return true
      }
      if (field.entityFields && isAnyFieldChecked(field.entityFields)) {
        return true
      }
    }
    return false
  }

  const mergeFields = (_currentFields, _newFields, { schemaType } = {}) => {
    const currentFields = safeStructuredClone(_currentFields)
    const newFields = safeStructuredClone(_newFields)

    for (const field of newFields) {
      const index = currentFields.findIndex((f) => f.name === field.name)

      if (field.relatedEntityId && field.entityFields) {
        const existingSubFields = (index !== -1 ? currentFields[index].fields : []) ?? []
        const mergedSubFields = mergeFields(existingSubFields, field.entityFields, { schemaType })

        const clonedField = { name: field.name, relatedEntityId: field.relatedEntityId, fields: mergedSubFields }
        if (schemaType === SchemaType.COMMAND || schemaType === SchemaType.QUERY) {
          clonedField.cardinality = field.cardinality
        }

        if (field.checked || isAnyFieldChecked(field.entityFields)) {
          if (index === -1) {
            currentFields.push(clonedField)
          } else {
            currentFields[index] = { ...currentFields[index], ...clonedField }
          }
        } else {
          if (index !== -1) {
            currentFields.splice(index, 1)
          }
        }
      } else {
        if (field.checked && index === -1) {
          currentFields.push({ name: field.name })
        }
        if (!field.checked && index !== -1) {
          currentFields.splice(index, 1)
        }
      }
    }

    return currentFields
  }

  const updateFields = async (eventId, cardId, fields) => {
    const targetEventId = eventId || route.params.eventId
    if (!targetEventId) {
      return
    }

    const result = getCardAndSchema(targetEventId, cardId)

    if (result) {
      const { schemas, schemaIndex } = result
      const schemaType = schemas[schemaIndex].type
      schemas[schemaIndex].fields = mergeFields(schemas[schemaIndex].fields, fields, { schemaType })

      await workflowApi.updateSchemas({
        schemas,
        workflow: workflow.value
      })
    } else {
      const card = svgGraph.value[targetEventId]?.cards?.find((c) => c.id === cardId)
      if (!card) {
        return
      }

      const schemaType = card.cardType?.domainModelRole === DomainModelRole.COMMAND
        ? SchemaType.COMMAND
        : SchemaType.QUERY
      const schemaName = sanitizeCardDescription(card.description)

      const newSchema = {
        name: schemaName,
        type: schemaType,
        fields: mergeFields([], fields, { schemaType })
      }

      await workflowApi.addSchemaAndLinkCard({
        schema: newSchema,
        cardId,
        eventId: targetEventId,
        workflow: workflow.value,
        svgGraph: svgGraph.value
      })
    }
  }

  const toggleFieldFilter = async (event, cardId, fieldIndex, subFieldIndex) => {
    const result = getCardAndSchema(event.id, cardId)
    if (!result) {
      return
    }

    const { schemas, schemaIndex } = result
    const field = subFieldIndex === undefined
      ? schemas[schemaIndex].fields[fieldIndex]
      : schemas[schemaIndex].fields[fieldIndex].fields[subFieldIndex]

    field.type = field.type === FieldType.FILTER
      ? FieldType.NORMAL
      : FieldType.FILTER

    await workflowApi.updateSchemas({
      schemas,
      workflow: workflow.value
    })
  }

  const hideField = async (event, cardId, fieldIndex) => {
    return hideFieldAtPath(event, cardId, [fieldIndex])
  }

  const selectFieldCardinality = async (eventId, cardId, fieldIndex, cardinality) => {
    return selectCardinalityAtPath(eventId, cardId, [fieldIndex], cardinality)
  }

  const createCard = async (eventId, cardTypeId, text) => {
    await workflowApi.createCard(route.params.id, eventId, {
      cardTypeId,
      description: text
    })
  }

  const selectSourceEntity = async (schemaId, entityId) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    newSchemas[schemaIndex].entityId = entityId || null

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const selectRelatedEntity = async (relatedEntity, schemaId, fieldIndex) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    newSchemas[schemaIndex].fields[fieldIndex].relatedEntityId = relatedEntity?.id || null

    if (relatedEntity) {
      newSchemas[schemaIndex].fields[fieldIndex].dataType = DataType.OBJECT
    } else {
      newSchemas[schemaIndex].fields[fieldIndex].dataType = DataType.STRING
    }

    if (!newSchemas[schemaIndex].fields[fieldIndex].cardinality) {
      newSchemas[schemaIndex].fields[fieldIndex].cardinality = 'one-to-one'
    }

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const setFieldCardinality = async (schemaId, fieldIndex, cardinality) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }
    const field = newSchemas[schemaIndex].fields[fieldIndex]

    field.cardinality = cardinality

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const deleteSchemaField = async (schemaId, fieldIndex) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    newSchemas[schemaIndex].fields.splice(fieldIndex, 1)

    await workflowApi.updateSchemas({
      schemas: newSchemas,
      workflow: workflow.value
    })
  }

  const renameSchemaField = async (schemaId, fieldIndex, newName) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const trimmed = newName.trim()
    if (!trimmed || trimmed.length > 30) {
      return
    }
    if (newSchemas[schemaIndex].fields[fieldIndex].name === trimmed) {
      return
    }

    newSchemas[schemaIndex].fields[fieldIndex].name = trimmed
    await workflowApi.updateSchemas({ schemas: newSchemas, workflow: workflow.value })
  }

  const renameSchemaSubField = async (schemaId, fieldIndex, subFieldIndex, newName) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const field = newSchemas[schemaIndex].fields[fieldIndex]
    if (!field?.fields?.[subFieldIndex]) {
      return
    }

    const trimmed = newName.trim()
    if (!trimmed || trimmed.length > 30) {
      return
    }
    if (field.fields[subFieldIndex].name === trimmed) {
      return
    }

    field.fields[subFieldIndex].name = trimmed
    await workflowApi.updateSchemas({ schemas: newSchemas, workflow: workflow.value })
  }

  const addSchemaField = async (schemaId) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    if (!newSchemas[schemaIndex].fields) {
      newSchemas[schemaIndex].fields = []
    }

    newSchemas[schemaIndex].fields.push({
      name: 'NEW FIELD ' + (newSchemas[schemaIndex].fields.length + 1)
    })
    await workflowApi.updateSchemas({ schemas: newSchemas, workflow: workflow.value })
  }

  const addSchemaSubField = async (schemaId, fieldIndex) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const field = newSchemas[schemaIndex].fields[fieldIndex]
    if (!field) {
      return
    }

    if (!field.fields) {
      field.fields = []
    }
    field.fields.push({
      name: 'NEW SUB FIELD ' + (field.fields.length + 1)
    })
    await workflowApi.updateSchemas({ schemas: newSchemas, workflow: workflow.value })
  }

  const removeSchemaSubField = async (schemaId, fieldIndex, subFieldIndex) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const field = newSchemas[schemaIndex].fields[fieldIndex]
    if (!field?.fields) {
      return
    }

    field.fields.splice(subFieldIndex, 1)
    await workflowApi.updateSchemas({ schemas: newSchemas, workflow: workflow.value })
  }

  const addSchemaFieldAtPath = async (schemaId, path = []) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    if (path.length === 0) {
      if (!newSchemas[schemaIndex].fields) {
        newSchemas[schemaIndex].fields = []
      }
      newSchemas[schemaIndex].fields.push({
        name: 'NEW FIELD ' + (newSchemas[schemaIndex].fields.length + 1)
      })
    } else {
      const parent = getFieldAtPath(newSchemas[schemaIndex].fields, path)
      if (!parent) {
        return
      }
      if (!parent.fields) {
        parent.fields = []
      }
      parent.fields.push({
        name: 'NEW SUB FIELD ' + (parent.fields.length + 1)
      })
    }

    await workflowApi.updateSchemas({ schemas: newSchemas, workflow: workflow.value })
  }

  const renameSchemaFieldAtPath = async (schemaId, path, newName) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const field = getFieldAtPath(newSchemas[schemaIndex].fields, path)
    if (!field) {
      return
    }

    const trimmed = newName.trim()
    if (!trimmed || trimmed.length > 30) {
      return
    }
    if (field.name === trimmed) {
      return
    }

    field.name = trimmed
    await workflowApi.updateSchemas({ schemas: newSchemas, workflow: workflow.value })
  }

  const removeSchemaFieldAtPath = async (schemaId, path) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1 || !path.length) {
      return
    }

    const parentPath = path.slice(0, -1)
    const fieldIndex = path[path.length - 1]
    const parent = parentPath.length === 0
      ? newSchemas[schemaIndex]
      : getFieldAtPath(newSchemas[schemaIndex].fields, parentPath)
    if (!parent?.fields) {
      return
    }

    parent.fields.splice(fieldIndex, 1)
    await workflowApi.updateSchemas({ schemas: newSchemas, workflow: workflow.value })
  }

  const selectRelatedEntityAtSchemaPath = async (relatedEntity, schemaId, path) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const field = getFieldAtPath(newSchemas[schemaIndex].fields, path)
    if (!field) {
      return
    }

    field.relatedEntityId = relatedEntity?.id || null

    if (relatedEntity) {
      field.dataType = DataType.OBJECT
    } else {
      field.dataType = DataType.STRING
    }

    if (!field.cardinality) {
      field.cardinality = 'one-to-one'
    }

    await workflowApi.updateSchemas({ schemas: newSchemas, workflow: workflow.value })
  }

  const setSchemaFieldCardinalityAtPath = async (schemaId, path, cardinality) => {
    const newSchemas = safeStructuredClone(workflow.value.schemas || [])
    const schemaIndex = newSchemas.findIndex((s) => s.id === schemaId)
    if (schemaIndex === -1) {
      return
    }

    const field = getFieldAtPath(newSchemas[schemaIndex].fields, path)
    if (!field) {
      return
    }

    field.cardinality = cardinality
    await workflowApi.updateSchemas({ schemas: newSchemas, workflow: workflow.value })
  }

  return {
    validateFieldName,
    cardTypeColor,
    getCommandCardHeight,
    getReadModelCardHeight,
    getDomainEventCardHeight,
    getQueryCardHeight,
    getSchemaFieldsForCard,
    removeCardAndModel,
    getRelatedEntity,
    hasFieldIssue,
    addFieldAtPath,
    blurFieldAtPath,
    removeFieldAtPath,
    selectRelatedEntityAtPath,
    selectCardinalityAtPath,
    hideFieldAtPath,
    addSubField,
    addSubSubField,
    blurField,
    blurSubField,
    blurSubSubField,
    removeSubField,
    removeSubSubField,
    onSelectRelatedEntity,
    onSelectRelatedSubEntity,
    selectSubFieldCardinality,
    removeField,
    addField,
    mergeFields,
    updateFields,
    hideField,
    getMatchingFieldInEntity,
    toggleFieldFilter,
    selectFieldCardinality,
    createCard,
    selectSourceEntity,
    selectRelatedEntity,
    setFieldCardinality,
    deleteSchemaField,
    renameSchemaField,
    renameSchemaSubField,
    addSchemaField,
    addSchemaSubField,
    removeSchemaSubField,
    addSchemaFieldAtPath,
    renameSchemaFieldAtPath,
    removeSchemaFieldAtPath,
    selectRelatedEntityAtSchemaPath,
    setSchemaFieldCardinalityAtPath,
    loadingDeleteModel
  }
}

export const setPendingSchemaChange = (change) => {
  pendingSchemaChange = change
}

export { shouldDeferSchemaUpdate, applyPendingChanges }
export default useDomainModel
